import React, { useState } from 'react';
import { Card, Button, Typography, Space, Select, Alert, message, Spin } from 'antd';
import { ApiOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const CloverConnectionSetup = ({ onConnectionComplete, onCancel }) => {
  const [environment, setEnvironment] = useState('sandbox');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('setup'); // setup, connecting, success

  const handleConnect = async () => {
    try {
      setLoading(true);
      setStep('connecting');

      // Generate OAuth URL using pos-integrations endpoint
      const response = await axios.post('/pos-integrations/clover/auth-url', {
        environment: environment
      }, {
        headers: {
          'Content-Type': 'application/json',
          'auth-token': localStorage.getItem('auth-token')
        }
      });

      const result = response.data;
      
      if (result.success) {
        // Redirect to Clover OAuth
        window.location.href = result.data.authUrl;
      } else {
        message.error(result.error || 'Failed to initiate Clover connection');
        setStep('setup');
      }
    } catch (error) {
      console.error('Error connecting to Clover:', error);
      const errorMessage = error.response?.data?.error || 'Failed to connect to Clover. Please try again.';
      message.error(errorMessage);
      setStep('setup');
    } finally {
      setLoading(false);
    }
  };

  const renderSetupStep = () => (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div style={{ textAlign: 'center' }}>
          <ApiOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
          <Title level={3}>Connect to Clover POS</Title>
          <Paragraph style={{ color: '#666', maxWidth: 500, margin: '0 auto' }}>
            Connect your Clover POS system to import sales data, inventory, customers, 
            and other business information into your workflows.
          </Paragraph>
        </div>

        <div style={{ maxWidth: 400, margin: '0 auto', width: '100%' }}>
          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Environment</Text>
            <Select
              value={environment}
              onChange={setEnvironment}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="sandbox">Sandbox (Testing)</Option>
              <Option value="production">Production (Live)</Option>
            </Select>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              Use sandbox for testing with demo data
            </Text>
          </div>

          <Alert
            message="Authorization Required"
            description="You will be redirected to Clover to authorize this application. Please ensure you have a Clover merchant account and the necessary permissions."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
{/* 
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>What data will be accessible:</Title>
            <ul style={{ color: '#666', paddingLeft: 20 }}>
              <li>Inventory items and categories</li>
              <li>Sales orders and transactions</li>
              <li>Customer information</li>
              <li>Payment data</li>
              <li>Business analytics</li>
            </ul>
          </div> */}

          <Space style={{ width: '100%', justifyContent: 'center' }}>
            <Button onClick={onCancel} size="large">
              Cancel
            </Button>
            <Button 
              type="primary" 
              size="large"
              onClick={handleConnect}
              loading={loading}
              icon={<ApiOutlined />}
            >
              Connect to Clover
            </Button>
          </Space>
        </div>
      </Space>
    </Card>
  );

  const renderConnectingStep = () => (
    <Card>
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <Spin size="large" style={{ marginBottom: 24 }} />
        <Title level={4}>Connecting to Clover...</Title>
        <Paragraph style={{ color: '#666' }}>
          You are being redirected to Clover for authorization. 
          Please complete the login process and grant permissions.
        </Paragraph>
        <Alert
          message="If the redirect doesn't work"
          description="Please check that pop-ups are enabled for this site, or try refreshing the page."
          type="warning"
          showIcon
          style={{ maxWidth: 400, margin: '24px auto 0' }}
        />
      </div>
    </Card>
  );

  const renderSuccessStep = () => (
    <Card>
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 24 }} />
        <Title level={3}>Successfully Connected!</Title>
        <Paragraph style={{ color: '#666', marginBottom: 32 }}>
          Your Clover POS system has been connected. You can now use this connection in your workflows.
        </Paragraph>
        <Button 
          type="primary" 
          size="large"
          onClick={onConnectionComplete}
        >
          Continue to Workflows
        </Button>
      </div>
    </Card>
  );

  // Check URL params for OAuth callback results
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const connectionId = urlParams.get('connectionId');

    if (success === 'clover_connected') {
      setStep('success');
      message.success('Clover connection established successfully!');
      
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (error) {
      const errorMessage = urlParams.get('message') || 'Connection failed';
      message.error(`Clover connection failed: ${decodeURIComponent(errorMessage)}`);
      setStep('setup');
      
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  switch (step) {
    case 'connecting':
      return renderConnectingStep();
    case 'success':
      return renderSuccessStep();
    default:
      return renderSetupStep();
  }
};

export default CloverConnectionSetup;